import React, { FC, useContext, useMemo } from 'react';
import styles from './index.module.scss';
import { DateContext } from '../../../index';
import { IndexTimeActive } from '../../SwitchDate';
import { EchartsWithTheme, Empty, useTheme } from '@maxtropy/components';
import { Spin } from 'antd';
import { useRequest } from 'ahooks';
import dayjs from 'dayjs';
import { apiV2PaintingDashboardTrendPost } from '@maxtropy/device-customer-apis-v2';
import { getHighEnergyTrendChartOption } from './charts';

const COLORS = ['#52E7FF', '#2D8DFF', '#FFCB47'];

const HighEnergyTrend: FC = () => {
  const theme = useTheme();
  const { activeKey, selectedDate } = useContext(DateContext);

  const requestParams = useMemo(() => {
    const timeResolution = activeKey === IndexTimeActive.THIS_MONTH ? 'M' : 'Y';
    const unit = activeKey === IndexTimeActive.THIS_MONTH ? 'month' : 'year';
    const startTime = selectedDate.startOf(unit).valueOf();
    // 结束时间：前一天23:59:59（如果是当前月份/年份），否则为月末/年末
    const isCurrentPeriod =
      selectedDate.format(activeKey === IndexTimeActive.THIS_MONTH ? 'YYYY-MM' : 'YYYY') ===
      dayjs().format(activeKey === IndexTimeActive.THIS_MONTH ? 'YYYY-MM' : 'YYYY');
    const endTime = isCurrentPeriod
      ? dayjs().subtract(1, 'day').endOf('day').valueOf()
      : selectedDate.endOf(unit).valueOf();
    return { startTime, endTime, timeResolution };
  }, [activeKey, selectedDate]);

  const { data, loading } = useRequest(() => apiV2PaintingDashboardTrendPost(requestParams), {
    refreshDeps: [requestParams],
  });

  // 转换数据格式
  const trendData = useMemo(() => {
    if (!data?.trends?.length) return [];

    // 获取所有时间点（以第一个车间的时间点为基准）
    const firstTrend = data.trends[0];
    if (!firstTrend?.points?.length) return [];

    // 构建时间轴
    const timestamps = firstTrend.points.map(p => p.timestamp ?? 0);

    // 构建每个时间点的数据
    return timestamps.map((ts, i) => {
      const item: { date: number; values: (number | null)[] } = {
        date: ts,
        values: [],
      };

      // 按车间编号排序获取对应值
      const sortedTrends = [...(data.trends ?? [])].sort((a, b) => (a.workshop ?? 0) - (b.workshop ?? 0));
      sortedTrends.forEach(trend => {
        item.values.push(trend.points?.[i]?.value ?? null);
      });

      return item;
    });
  }, [data]);

  // 构建图例数据
  const legendData = useMemo(() => {
    if (!data?.trends?.length) return [];
    const chineseNumbers = ['一', '二', '三', '四', '五', '六', '七', '八', '九'];
    const sortedTrends = [...(data.trends ?? [])].sort((a, b) => (a.workshop ?? 0) - (b.workshop ?? 0));
    return sortedTrends.map((trend, i) => ({
      name: trend.workshop ? `涂装${chineseNumbers[trend.workshop - 1] ?? '--'}期` : '--',
      color: COLORS[i % COLORS.length],
    }));
  }, [data]);

  const trendChartOption = useMemo(() => {
    if (!trendData.length || !legendData.length) return null;
    // 根据时间粒度格式化 x 轴日期
    // 按年：YYYY-MM，按月：MM-DD
    const dateFormat = activeKey === IndexTimeActive.THIS_MONTH ? 'MM-DD' : 'YYYY-MM';
    return getHighEnergyTrendChartOption(trendData, legendData, dateFormat);
  }, [trendData, legendData, activeKey]);

  if (loading) {
    return (
      <div className={styles.trend_content}>
        <div className={styles.loading_wrapper}>
          <Spin />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.trend_content}>
      <div className={styles.trend_chart}>
        <EchartsWithTheme
          theme={theme}
          style={{ height: '100%', width: '100%' }}
          option={trendChartOption}
          empty={{ isShowEmpty: !trendData.length, emptyNode: <Empty /> }}
        />
      </div>
    </div>
  );
};

export default HighEnergyTrend;
